/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnInit, signal} from '@angular/core';
import {UIRouter} from '@uirouter/core';

import template from './tfa-recovery-page.component.html';
import {AuthenticatedUserService} from '../../common/controller/authenticated-user.service';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {TwoFactorAuthRecoveryApiService} from '../../common/controller/two-factor-auth-recovery-api.service';

@Component({
  standalone: false,
  selector: 'adn-tfa-recovery-page',
  template
})
export class TfaRecoveryPageComponent implements OnInit {
  readonly verifyCompleted = signal(false);
  readonly alreadyCompleted = signal(false);
  readonly verifyProcess = signal(false);
  verifyCode: string | null = null;
  readonly userNotFound = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly user = signal<any>(null);
  readonly isEmail2fa = signal(false);
  readonly codeSent = signal(false);
  readonly sendingEmailCode = signal(false);

  constructor(
    private readonly twoFactorAuthRecoveryApi: TwoFactorAuthRecoveryApiService,
    private readonly authenticatedUserResource: AuthenticatedUserService,
    @Inject(LocalUserProfile) private readonly localUserProfile: LocalUserProfile,
    private readonly uiRouter: UIRouter
  ) {
  }

  ngOnInit(): void {
    this.isEmail2fa.set(this.localUserProfile.get('twoFactorAuthType') === 'EMAIL'
      && this.localUserProfile.get('login2faRequired') === true);
    this.twoFactorAuthRecoveryApi.getRecoveryUser(this.uiRouter.globals.params.userId).subscribe((page: any) => {
      if (page.status === 404) {
        this.userNotFound.set(true);
      } else if (page.status === 400) {
        this.alreadyCompleted.set(true);
        this.errorMessage.set(page.data.text);
      } else {
        this.user.set(page);
        this.verifyProcess.set(true);
        if (this.isEmail2fa()) {
          this.sendEmailCode();
        }
      }
    });
  }

  sendEmailCode(): void {
    this.sendingEmailCode.set(true);
    this.codeSent.set(false);
    this.authenticatedUserResource.sendEmail2faCode('2fa-recovery').then((result: any) => {
      if (result === true) {
        this.codeSent.set(true);
        this.errorMessage.set(null);
      } else {
        this.errorMessage.set(result.data?.text || result.data?.message || result.data?.code || 'Unable to send a verification code.');
      }
      this.sendingEmailCode.set(false);
    }).catch((result: any) => {
      this.errorMessage.set(result?.data?.text || result?.data?.message || 'Unable to send a verification code.');
      this.sendingEmailCode.set(false);
    });
  }

  verify(): void {
    this.errorMessage.set(null);
    this.twoFactorAuthRecoveryApi.submitVerifiedUser(this.user().id, this.verifyCode).subscribe((result: any) => {
      if (result === true) {
        this.verifyCompleted.set(true);
        this.verifyProcess.set(false);
      } else {
        this.errorMessage.set(result.text);
      }
    });
  }
}
